import React, { Component } from 'react';
import { observable, action, computed, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import { FormCheckbox, FormSelect, FormInput, FormTextarea, Button, Badge, Row, Col, Card, CardBody } from "shards-react";
import FeatherIcon from 'feather-icons-react';
import moment from 'moment';
import PageTitle from "../../../components/common/PageTitle";
import { toast } from "react-toastify";
import './integratedMessage.scss';
import '../ReviewBooster/style.scss';

const TABS = [
  { key: 'mileage', label: '마일리지', icon: 'gift' },
  { key: 'coupon', label: '쿠폰', icon: 'tag' },
  { key: 'delivery', label: '배송', icon: 'truck' },
  { key: 'review', label: '리뷰', icon: 'message-square' },
  { key: 'history', label: '내역', icon: 'list' },
];

// 8개 메시지 타입 (4개 탭으로 분류)
const TYPES = [
  { key: 'mileageAccrual',   tab: 'mileage',  title: '마일리지 적립 안내', desc: '고객에게 마일리지가 적립되면 발송', vars: ['shop', 'name', 'amount'], extra: null },
  { key: 'mileageExpire',    tab: 'mileage',  title: '마일리지 소멸 안내', desc: '적립 마일리지의 소멸 예정일 전에 발송 (온리플 리뷰 적립분 대상)', vars: ['shop', 'name', 'amount', 'expiryDate'], extra: 'mileageExpire' },
  { key: 'couponIssue',      tab: 'coupon',   title: '쿠폰 지급 안내',     desc: '쿠폰이 지급되면 발송 (리뷰작성/첫구매 시 자동 발급)', vars: ['shop', 'name', 'couponName', 'expiryDate'], extra: 'coupon' },
  { key: 'couponExpire',     tab: 'coupon',   title: '쿠폰 만료 안내',     desc: '쿠폰 만료 예정일 전에 발송', vars: ['shop', 'name', 'couponName', 'expiryDate'], extra: 'daysBefore' },
  { key: 'deliveryReady',    tab: 'delivery', title: '배송 준비 안내',     desc: '배송 준비 상태가 되면 발송', vars: ['shop', 'name', 'product'], extra: null },
  { key: 'deliveryShipping', tab: 'delivery', title: '배송중 안내',        desc: '배송이 시작되면 발송', vars: ['shop', 'name', 'product', 'tracking'], extra: null },
  { key: 'deliveryDone',     tab: 'delivery', title: '배송 완료 안내',     desc: '배송이 완료되면 발송', vars: ['shop', 'name', 'product'], extra: null },
  { key: 'reviewReply',      tab: 'review',   title: '리뷰 답글 안내',     desc: '내가 쓴 리뷰에 판매자 답글이 달리면 발송', vars: ['shop', 'name', 'product', 'reply'], extra: null },
];

const VAR_DESC = { shop: '스토어명', name: '고객명', amount: '금액', expiryDate: '만료일', couponName: '쿠폰명', product: '상품명', tracking: '운송장번호', reply: '답글내용' };
const SAMPLE = { shop: '온리플샵', name: '홍길동', amount: '500', expiryDate: '2026-07-01', couponName: '여름 10% 쿠폰', product: '반바지 KK904', tracking: '1234567890', reply: '소중한 리뷰 감사합니다!' };

// 항목별 기본 알림톡 템플릿 샘플 (정보성 메시지 — 카카오 알림톡은 광고성 불가, 사전검수 필요)
const SAMPLE_TEMPLATES = {
  mileageAccrual:   "[#{shop}] 적립금 적립 안내\n\n#{name}님, #{amount}원의 적립금이 적립되었습니다.\n마이페이지 > 적립금에서 내역을 확인하실 수 있습니다.",
  mileageExpire:    "[#{shop}] 적립금 소멸 예정 안내\n\n#{name}님, 보유하신 적립금 #{amount}원이 #{expiryDate}에 소멸될 예정입니다.\n소멸 전 마이페이지에서 확인해 주세요.",
  couponIssue:      "[#{shop}] 쿠폰 발급 안내\n\n#{name}님께 '#{couponName}' 쿠폰이 발급되었습니다.\n유효기간: ~#{expiryDate}\n마이페이지 > 쿠폰함에서 확인하실 수 있습니다.",
  couponExpire:     "[#{shop}] 쿠폰 만료 예정 안내\n\n#{name}님, 보유하신 '#{couponName}' 쿠폰이 #{expiryDate}에 만료됩니다.\n쿠폰함에서 사용 여부를 확인해 주세요.",
  deliveryReady:    "[#{shop}] 배송 준비 안내\n\n#{name}님, 주문하신 '#{product}' 상품의 배송 준비가 시작되었습니다.\n곧 발송될 예정입니다.",
  deliveryShipping: "[#{shop}] 배송 시작 안내\n\n#{name}님, 주문하신 '#{product}' 상품이 발송되었습니다.\n운송장번호: #{tracking}\n배송 조회에서 위치를 확인하실 수 있습니다.",
  deliveryDone:     "[#{shop}] 배송 완료 안내\n\n#{name}님, 주문하신 '#{product}' 상품이 배송 완료되었습니다.\n수령 후 이상이 있으면 고객센터로 문의해 주세요.",
  reviewReply:      "[#{shop}] 리뷰 답글 안내\n\n#{name}님, 작성하신 '#{product}' 상품 리뷰에 판매자 답글이 등록되었습니다.\n답글 내용을 확인해 보세요.",
};

const ISSUE_TRIGGERS = [
  { key: 'reviewWritten', label: '리뷰 작성 시' },
  { key: 'firstPurchase', label: '첫 구매 시' },
  { key: 'manual', label: '수동 발급' },
];

const CHANNELS = [
  { key: 'sms', label: '문자', enabled: true },
  { key: 'kakao', label: '알림톡', enabled: true },
  { key: 'webpush', label: '웹푸시', enabled: false }, // Phase 2
];

// 임시: 문자(SMS) 발송 준비 중 — UI 미노출. true 동안 문자 채널/에디터/테스트/기본채널 모두 숨김. (준비 완료 시 false)
const SMS_HIDDEN = true;

function applyVars(tpl, channel, shop) {
  const re = channel === 'kakao' ? /#\{(\w+)\}/g : /\{(\w+)\}/g;
  const vars = shop ? Object.assign({}, SAMPLE, { shop: shop }) : SAMPLE;
  return String(tpl == null ? '' : tpl).replace(re, (m, k) => (vars[k] != null ? vars[k] : m));
}

@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@inject(({ common }) => ({ getDoc: common.getDoc, updateDoc: common.updateDoc, base64UploadImages: common.base64UploadImages }))
@inject(({ booster }) => ({ getBoosterSetting: booster.getBoosterSetting, setKakaoTemp: booster.setKakaoTemp }))
@observer
class IntegratedMessage extends Component {

  @observable activeTab = 'mileage';
  @observable history = [];              // 발송 내역 (내역 탭)
  @observable historyLoaded = false;
  @observable historyLoading = false;
  @observable historyPage = 1;
  @observable historyTotalPages = 1;
  @observable loaded = false;
  @observable saving = false;
  @observable dirty = false;
  @observable settingsSnapshot = null; // 저장 시점 스냅샷(JSON) — wbp-sticky-save dirty 추적용 (원래값 복귀 시 자동 해제)
  @observable types = {};   // types[typeKey] = { enabled, channels, sms, kakao, daysBefore, couponNo, couponName, issueTrigger }
  @observable coupons = []; // 카페24 발급가능 쿠폰 목록 (쿠폰 탭 선택용)
  @observable alimTemplates = []; // 알림톡 승인 템플릿 (BoosterSetting.temp_N)
  @observable teamUser = {};
  @observable kakaoAuthed = false;       // 카카오 채널(BoosterSetting.isKakaoPlusidAuth) 연결 여부
  @observable kakaoPlusid = '';          // 카카오 채널 ID (상단 채널정보)
  @observable kakaoPhone = '';           // 카카오 채널 관리자 번호
  @observable kakaoEditorForType = null; // 알림톡 템플릿 작성 에디터가 열린 타입 key
  @observable kakaoNewContent = '';      // 작성 중인 템플릿 내용
  @observable kakaoTplSaving = false;    // 등록 진행 중 플래그
  @observable kakaoNewButtonName = '';   // 작성 중 버튼명 (선택)
  @observable kakaoNewButtonLink = '';   // 작성 중 버튼 링크 (선택)
  @observable kakaoEmphasisType = 'none';  // 강조유형: none(기본)/text(강조표기형)/image(이미지)
  @observable kakaoEmTitle = '';           // 강조 제목 (강조표기형)
  @observable kakaoEmSubtitle = '';        // 강조 부제목 (강조표기형)
  @observable kakaoImageUrl = '';          // 업로드된 강조 이미지 URL (이미지형, 카페24 호스팅)
  @observable kakaoImageUploading = false; // 이미지 업로드 진행 중
  @observable testPhone = '';            // 테스트 발송 받을 번호 (공용)
  @observable testSendingKey = null;     // 'typeKey:channel' — 발송 진행 중 표시

  constructor(props) {
    super(props);
    this.kakaoFileRef = React.createRef();
    this.init();
  }

  componentDidMount() {
    // 미저장 변경사항 이탈 가드 (위젯/카카오 설정과 동일)
    this._beforeUnload = (e) => {
      if (this.settingsDirty) { e.preventDefault(); e.returnValue = '저장되지 않은 변경사항이 있습니다. 정말 떠나시겠습니까?'; return e.returnValue; }
    };
    window.addEventListener('beforeunload', this._beforeUnload);
  }

  componentWillUnmount() {
    if (this._beforeUnload) window.removeEventListener('beforeunload', this._beforeUnload);
  }

  _default = () => ({ enabled: false, channels: SMS_HIDDEN ? ['kakao'] : ['sms'], sms: { content: '' }, kakao: { templateCode: '', content: '' }, daysBefore: 7, validityMonths: 12, couponNo: '', couponName: '', issueTrigger: 'reviewWritten' });

  @action init = async () => {
    if (!this.props.isLogin) return;
    this.teamUser = this.props.me;
    const store_id = this.teamUser.selStore_id;
    const shopNo = this.teamUser.selShopNo ? this.teamUser.selShopNo : 1;
    const res = await this.props.getDoc('getIntegratedMessageConfig', { store_id, shopNo });
    const saved = (res && res.data && res.data.types) ? res.data.types : {};
    const merged = {};
    TYPES.forEach(t => {
      const def = this._default();
      // 미설정(신규) 항목 — 문자 내용을 기본 샘플로 미리 채워, 사용자가 그 구조에서 바로 수정해서 쓸 수 있게
      if (!saved[t.key] && SAMPLE_TEMPLATES[t.key]) {
        def.sms = { content: SAMPLE_TEMPLATES[t.key].replace(/#\{/g, '{') };
      }
      merged[t.key] = Object.assign(def, saved[t.key] || {});
    });
    this.types = merged;
    this.settingsSnapshot = JSON.stringify(merged);
    this.loaded = true;

    // 쿠폰 목록(발급가능) — 쿠폰 탭 선택용. 실패해도 무시(직접 입력 폴백).
    try {
      const cres = await this.props.getDoc('getCafe24Coupons', { store_id, shopNo });
      this.coupons = (cres && Array.isArray(cres.data)) ? cres.data : [];
    } catch (e) { this.coupons = []; }

    // 알림톡 템플릿(BoosterSetting.temp_0~5) — kakao 후보/선택용.
    await this.loadAlimTemplates(store_id, shopNo);
  };

  // BoosterSetting 의 알림톡 템플릿 슬롯 + 채널 인증 상태를 로드 (등록/삭제 후 재호출)
  loadAlimTemplates = async (store_id, shopNo) => {
    try {
      const bres = await this.props.getBoosterSetting({ store_id, shopNo });
      const bs = (bres && bres.data) ? bres.data : null;
      const temps = [];
      if (bs) {
        for (let i = 0; i <= 5; i++) {
          const tp = bs['temp_' + i];
          if (tp && tp.templtCode) temps.push({ tempIndex: 'temp_' + i, templtCode: tp.templtCode, templtName: tp.templtName, inspStatus: tp.inspStatus, templtContent: tp.templtContent });
        }
      }
      this.alimTemplates = temps;
      this.kakaoAuthed = !!(bs && bs.isKakaoPlusidAuth);
      this.kakaoPlusid = (bs && bs.kakaoPlusid) || '';
      this.kakaoPhone = (bs && bs.phonenumber) || '';
    } catch (e) { this.alimTemplates = []; this.kakaoAuthed = false; this.kakaoPlusid = ''; this.kakaoPhone = ''; }
  };

  // 항목별 알림톡 템플릿 등록 — Aligo 등록(updateDoc) 후 그 항목 templates 에 추가 → 표준 저장 바로 저장
  addKakaoTemplate = (typeKey) => {
    const content = (this.kakaoNewContent || '').trim();
    if (!this.kakaoAuthed) { toast.error('카카오 채널을 먼저 연결해 주세요.'); return; }
    if (!content) { toast.error('템플릿 내용을 입력해 주세요.'); return; }
    if (this.kakaoEmphasisType === 'text' && !(this.kakaoEmTitle || '').trim()) { toast.error('강조 제목을 입력해 주세요.'); return; }
    if (this.kakaoEmphasisType === 'text' && !(this.kakaoEmSubtitle || '').trim()) { toast.error('강조 부제목을 입력해 주세요. (강조표기형 필수)'); return; }
    if (this.kakaoEmphasisType === 'image' && !this.kakaoImageUrl) { toast.error('이미지를 업로드해 주세요.'); return; }
    const cur = (this.types[typeKey] && this.types[typeKey].kakao && Array.isArray(this.types[typeKey].kakao.templates)) ? this.types[typeKey].kakao.templates : [];
    if (cur.length >= 3) { toast.error('항목별 템플릿은 최대 3개입니다.'); return; }
    if (this.kakaoTplSaving) return;
    this.kakaoTplSaving = true;
    const store_id = this.teamUser.selStore_id;
    const shopNo = this.teamUser.selShopNo ? this.teamUser.selShopNo : 1;
    let _this = this;
    this.props.updateDoc('addIntegratedMsgKakaoTemplate', {
      store_id, shopNo, typeKey,
      templtContent: content,
      buttonName: (this.kakaoNewButtonName || '').trim(),
      buttonLink: (this.kakaoNewButtonLink || '').trim(),
      emphasisType: this.kakaoEmphasisType,
      emphasizeTitle: (this.kakaoEmTitle || '').trim(),
      emphasizeSubtitle: (this.kakaoEmSubtitle || '').trim(),
      imageUrl: this.kakaoImageUrl
    }, function(result, errors) {
      _this.kakaoTplSaving = false;
      if (errors && errors.length > 0) { toast.error(errors[0].message || '템플릿 등록에 실패했습니다.'); return; }
      const d = result && result.data;
      if (d && Array.isArray(d.errors) && d.errors.length > 0) { toast.error(d.errors[0].message || '템플릿 등록에 실패했습니다.'); return; }
      if (!d || !d.ok || !d.template) { toast.error('템플릿 등록에 실패했습니다.'); return; }
      const t = _this.types[typeKey] || {};
      const ka = Object.assign({}, t.kakao);
      ka.templates = (Array.isArray(ka.templates) ? ka.templates : []).concat([d.template]);
      _this.setField(typeKey, { kakao: ka });
      _this.resetKakaoEditor();
      _this.kakaoEditorForType = null;
      _this.persistConfig();
      toast.success('템플릿이 등록되었습니다. 카카오 심사 승인 후 [이 템플릿 사용]을 누르면 발송됩니다.');
    });
  };

  // 항목별 알림톡 템플릿 삭제 — Aligo 삭제(updateDoc) 후 그 항목 templates 에서 제거 → 자동 저장
  // 안전장치: (1) 심사중(REG/REQ)은 삭제 불가, (2) 고유 templtName 으로 정확히 1개만 제거(빈/중복 templtCode 다중삭제 방지)
  deleteKakaoTemplate = (typeKey, tp) => {
    const insp = tp && tp.inspStatus;
    if (insp === 'REG' || insp === 'REQ') { toast.error('심사 중인 템플릿은 삭제할 수 없습니다. 심사 완료(승인/반려) 후 삭제해 주세요.'); return; }
    const templtCode = tp && tp.templtCode;
    const templtName = tp && tp.templtName;
    if (!templtName) { toast.error('템플릿 정보를 찾을 수 없어 삭제할 수 없습니다.'); return; }
    if (!window.confirm('이 알림톡 템플릿을 삭제하시겠습니까?')) return;
    const store_id = this.teamUser.selStore_id;
    const shopNo = this.teamUser.selShopNo ? this.teamUser.selShopNo : 1;
    let _this = this;
    this.props.updateDoc('deleteIntegratedMsgKakaoTemplate', { store_id, shopNo, typeKey, templtCode }, function(result, errors) {
      if (errors && errors.length > 0) { toast.error(errors[0].message || '템플릿 삭제에 실패했습니다.'); return; }
      const d = result && result.data;
      if (d && Array.isArray(d.errors) && d.errors.length > 0) { toast.error(d.errors[0].message || '템플릿 삭제에 실패했습니다.'); return; }
      const t = _this.types[typeKey] || {};
      const ka = Object.assign({}, t.kakao);
      // 고유한 templtName 기준으로 정확히 해당 템플릿 1개만 제거
      ka.templates = (Array.isArray(ka.templates) ? ka.templates : []).filter(x => x.templtName !== templtName);
      if (templtCode && ka.templateCode === templtCode) ka.templateCode = '';
      _this.setField(typeKey, { kakao: ka });
      _this.persistConfig();
      toast.success('템플릿이 삭제되었습니다.');
    });
  };

  @action setField = (typeKey, patch) => {
    this.types[typeKey] = Object.assign({}, this.types[typeKey], patch);
    this.dirty = true;
  };
  @action toggleEnabled = (typeKey) => this.setField(typeKey, { enabled: !this.types[typeKey].enabled });
  @action toggleChannel = (typeKey, ch) => {
    const t = this.types[typeKey];
    const has = (t.channels || []).includes(ch);
    const channels = has ? t.channels.filter(c => c !== ch) : (t.channels || []).concat(ch);
    this.setField(typeKey, { channels });
  };
  @action setChannelContent = (typeKey, ch, content) => {
    const t = this.types[typeKey];
    this.setField(typeKey, { [ch]: Object.assign({}, t[ch], { content }) });
  };
  @action insertVar = (typeKey, ch, v) => {
    const t = this.types[typeKey];
    const cur = (t[ch] && t[ch].content) || '';
    const token = ch === 'kakao' ? '#{' + v + '}' : '{' + v + '}';
    this.setChannelContent(typeKey, ch, cur + token);
  };

  // wbp-sticky-save: 스냅샷 기준 dirty (원래값으로 되돌리면 자동 해제 — 위젯 설정과 동일)
  @computed get settingsDirty() {
    if (!this.loaded || this.settingsSnapshot == null) return false;
    return JSON.stringify(toJS(this.types)) !== this.settingsSnapshot;
  }

  // 변경된 메시지 항목 제목 목록 (저장 바 태그용)
  getDirtyLabels = () => {
    if (this.settingsSnapshot == null) return [];
    let snap;
    try { snap = JSON.parse(this.settingsSnapshot); } catch (e) { return []; }
    const cur = toJS(this.types);
    const labels = [];
    TYPES.forEach(t => {
      if (JSON.stringify(cur[t.key]) !== JSON.stringify(snap[t.key])) labels.push(t.title);
    });
    return labels;
  };

  @action revertSettings = () => {
    if (this.settingsSnapshot == null) return;
    try { this.types = JSON.parse(this.settingsSnapshot); } catch (e) {}
  };

  // 템플릿 추가/사용/삭제 후 별도 '변경사항 저장' 없이 설정을 자동 저장 (스냅샷 갱신 → 저장 바 자동 해제)
  persistConfig = async (successMsg) => {
    const store_id = this.teamUser.selStore_id;
    const shopNo = this.teamUser.selShopNo ? this.teamUser.selShopNo : 1;
    try {
      const res = await this.props.updateDoc('updateIntegratedMessageConfig', { store_id, shopNo, types: toJS(this.types) });
      if (res && res.errors && res.errors.length > 0) { toast.error('저장 실패: ' + res.errors[0].message); return false; }
      this.dirty = false;
      this.settingsSnapshot = JSON.stringify(toJS(this.types));
      if (successMsg) toast.success(successMsg);
      return true;
    } catch (e) {
      toast.error('저장 중 오류가 발생했습니다.');
      return false;
    }
  };

  save = async () => {
    if (this.saving) return;
    this.saving = true;
    const store_id = this.teamUser.selStore_id;
    const shopNo = this.teamUser.selShopNo ? this.teamUser.selShopNo : 1;
    try {
      const res = await this.props.updateDoc('updateIntegratedMessageConfig', { store_id, shopNo, types: toJS(this.types) });
      if (res && res.errors && res.errors.length > 0) { toast.error('저장 실패: ' + res.errors[0].message); return; }
      this.dirty = false;
      this.settingsSnapshot = JSON.stringify(toJS(this.types));
      toast.success('통합메시지 설정이 저장되었습니다.');
    } catch (e) {
      toast.error('저장 중 오류가 발생했습니다.');
    } finally {
      this.saving = false;
    }
  };

  // 알림톡 = 카카오 승인 템플릿. 이 메시지(항목) 전용 템플릿 최대 3개 — 생성/삭제/사용.
  // 작성 에디터 입력값 초기화 (열기/취소/등록 후)
  resetKakaoEditor = () => {
    this.kakaoNewContent = '';
    this.kakaoNewButtonName = '';
    this.kakaoNewButtonLink = '';
    this.kakaoEmphasisType = 'none';
    this.kakaoEmTitle = '';
    this.kakaoEmSubtitle = '';
    this.kakaoImageUrl = '';
  };

  // 강조 이미지 업로드 → 카페24 호스팅 URL 획득
  uploadKakaoImage = async (file) => {
    if (!file) return;
    const store_id = this.teamUser.selStore_id;
    this.kakaoImageUploading = true;
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      const res = await this.props.base64UploadImages({ store_id, base64Imgs: [{ image: b64 }] });
      if (res && Array.isArray(res.errors) && res.errors.length > 0) { toast.error(res.errors[0].message || '이미지 업로드에 실패했습니다.'); return; }
      if (res && res.images && res.images.length > 0 && res.images[0].path) {
        this.kakaoImageUrl = res.images[0].path;
        toast.success('이미지가 업로드되었습니다.');
      } else {
        toast.error('이미지 업로드에 실패했습니다. (카페24 연결 상태를 확인해 주세요)');
      }
    } catch (e) {
      toast.error('이미지 업로드 중 오류가 발생했습니다.');
    } finally {
      this.kakaoImageUploading = false;
    }
  };

  // 등록된 알림톡 템플릿을 카카오톡 말풍선 미리보기 형태로 렌더 (강조유형/이미지/버튼 반영)
  renderKakaoBubble = (d) => {
    const storeName = (this.teamUser && this.teamUser.store && this.teamUser.store.name) || '온리플샵';
    const content = applyVars(d.content || '', 'kakao', storeName);
    const hasText = d.emphasisType === 'text' && (d.emTitle || d.emSubtitle);
    const hasImage = d.emphasisType === 'image' && d.imageUrl;
    return (
      <div style={{background: '#b2c7da', borderRadius: 8, padding: '12px 11px', margin: '2px 0 10px', width: '100%', maxWidth: 320, boxSizing: 'border-box'}}>
        <div style={{fontSize: 11, color: '#33475b', fontWeight: 600, marginBottom: 5}}>{storeName}</div>
        <div style={{background: '#fff', borderRadius: '4px 16px 16px 16px', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.10)'}}>
          <div style={{background: '#FEE500', color: '#3c1e1e', fontSize: 10, fontWeight: 700, padding: '5px 11px', letterSpacing: '0.2px'}}>알림톡 도착</div>
          {hasImage ? (
            <div style={{lineHeight: 0}}><img src={d.imageUrl} alt="강조 이미지" style={{width: '100%', display: 'block'}} /></div>
          ) : null}
          <div style={{padding: '11px 13px'}}>
            {hasText ? (
              <div style={{marginBottom: 8}}>
                {d.emTitle ? <div style={{fontSize: 15, fontWeight: 700, color: '#1f2937', lineHeight: 1.35}}>{d.emTitle}</div> : null}
                {d.emSubtitle ? <div style={{fontSize: 12, color: '#94a3b8', marginTop: 2}}>{d.emSubtitle}</div> : null}
              </div>
            ) : null}
            <pre style={{margin: 0, fontFamily: 'inherit', fontSize: 13, lineHeight: 1.65, color: '#1f2937', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>{content || '-'}</pre>
          </div>
          {(d.buttonName || '').trim() ? (
            <div style={{borderTop: '1px solid #f1f1f1', textAlign: 'center', padding: '10px 12px', fontSize: 12.5, color: '#33475b', fontWeight: 500}}>{d.buttonName}</div>
          ) : null}
        </div>
      </div>
    );
  };

  renderKakaoEditor = (type) => {
    const t = this.types[type.key] || {};
    const ka = t.kakao || {};
    const code = ka.templateCode || '';
    const templates = (Array.isArray(ka.templates) ? ka.templates : []).slice(0, 3);
    const editing = this.kakaoEditorForType === type.key;
    // 발송 가능 상태: 사용 중(templateCode)인 템플릿이 카카오 승인(APR)된 경우에만 실제 발송됨
    const usedTpl = templates.find(tp => tp.templtCode === code);
    const hasActiveSending = !!(code && usedTpl && usedTpl.inspStatus === 'APR');
    const hasApr = templates.some(tp => tp.inspStatus === 'APR');
    const storeName = (this.teamUser && this.teamUser.store && this.teamUser.store.name) || '온리플샵';
    // kakaoMsgSetting 과 동일한 상태 배지 (REG/REQ: 심사중, APR: 승인완료/사용중, REJ: 반려)
    const statusBadge = (inspStatus, isUsed) => {
      switch (inspStatus) {
        case 'APR': return isUsed ? <Badge theme="primary">사용중</Badge> : <Badge outline theme="success">승인완료</Badge>;
        case 'REJ': return <Badge outline theme="warning">반려</Badge>;
        case 'REG':
        case 'REQ':
        default: return <Badge outline theme="info">심사중</Badge>;
      }
    };
    return (
      <div className="imsg-editor" key="kakao">
        <div className="imsg-editor-head">
          <span className="imsg-editor-ch">알림톡 템플릿 <em style={{fontStyle: 'normal', color: '#94a3b8', fontWeight: 400, fontSize: 11}}>(이 메시지 전용 · 카카오 승인 필요)</em></span>
          <span className="imsg-editor-note">승인된 템플릿만 발송됩니다. 이 항목에 최대 3개까지 만들고, 사용할 템플릿을 선택하세요.</span>
        </div>

        {!this.kakaoAuthed ? (
          <div className="imsg-empty-ch">카카오 채널이 연결되어 있지 않습니다. <a href="/kakaoMsgSetting">작성유도 &gt; 알림톡 설정</a>에서 먼저 채널을 연결해 주세요.</div>
        ) : (
          <>
            {templates.length > 0 && !hasActiveSending && (
              <div style={{display: 'flex', alignItems: 'flex-start', gap: 9, background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '11px 14px', margin: '4px 0 12px'}}>
                <FeatherIcon icon="alert-triangle" size="17" style={{color: '#B45309', flexShrink: 0, marginTop: 1}} aria-hidden="true" />
                <div style={{fontSize: 12.5, color: '#92400E', lineHeight: 1.6}}>
                  <b style={{fontSize: 13}}>현재 이 메시지는 알림톡으로 발송되지 않습니다.</b><br/>
                  {hasApr
                    ? <>승인된 템플릿을 아래에서 <b>[이 템플릿 사용]</b> 버튼으로 선택하셔야 발송이 시작됩니다.</>
                    : <>등록한 템플릿이 카카오 <b>심사 중</b>입니다. 심사 승인 후 <b>[이 템플릿 사용]</b>을 눌러 사용 설정을 해주셔야 발송됩니다.</>}
                </div>
              </div>
            )}
            {templates.length > 0 ? (
              <div className="imsg-tpl-grid">
                {templates.map(tp => {
                  const isUsed = !!code && code === tp.templtCode;
                  const apr = tp.inspStatus === 'APR';
                  return (
                    <div key={tp.templtName || tp.templtCode} style={{border: '1px solid ' + (isUsed ? '#10b981' : '#e2e8f0'), background: isUsed ? '#f0fdf4' : '#fff', borderRadius: 8, padding: '10px 12px'}}>
                      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6}}>
                        <span style={{fontWeight: 700, fontSize: 13, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{tp.templtName || tp.templtCode}</span>
                        <span style={{flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4}}>
                          {tp.emphasisType === 'image' ? <Badge outline theme="secondary" style={{fontSize: 10}}>이미지</Badge> : tp.emphasisType === 'text' ? <Badge outline theme="secondary" style={{fontSize: 10}}>강조표기</Badge> : null}
                          {statusBadge(tp.inspStatus, isUsed)}
                        </span>
                      </div>
                      {this.renderKakaoBubble({ content: tp.templtContent, emphasisType: tp.emphasisType, emTitle: tp.emphasizeTitle, emSubtitle: tp.emphasizeSubtitle, imageUrl: tp.imageUrl, buttonName: tp.buttonName })}
                      <div style={{display: 'flex', gap: 6}}>
                        <Button size="sm" theme={isUsed ? 'success' : 'light'} disabled={!apr}
                                onClick={() => { this.setField(type.key, { kakao: Object.assign({}, ka, { templateCode: isUsed ? '' : tp.templtCode }) }); this.persistConfig(isUsed ? '사용 해제되었습니다.' : '이 템플릿으로 발송 설정되었습니다.'); }}>
                          {isUsed ? '✓ 사용 중' : (apr ? '이 템플릿 사용' : '미승인')}
                        </Button>
                        <Button size="sm" theme="danger" outline
                                disabled={tp.inspStatus === 'REG' || tp.inspStatus === 'REQ'}
                                title={(tp.inspStatus === 'REG' || tp.inspStatus === 'REQ') ? '심사 중에는 삭제할 수 없습니다' : '템플릿 삭제'}
                                onClick={() => this.deleteKakaoTemplate(type.key, tp)}>삭제</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="imsg-empty-ch">이 항목에 등록된 알림톡 템플릿이 없습니다. 아래에서 새로 작성해 주세요.</div>
            )}

            {templates.length < 3 ? (
              editing ? (
                <div className="kakao-msg-redesign" style={{marginTop: 8}}>
                  <Row>
                    {/* 좌측: 작성 폼 (kakaoMsgSetting 과 동일 UI) */}
                    <Col lg="7" md="12">
                      <div className="template-edit-pane">
                        <div className="edit-pane-header">
                          <FeatherIcon icon="edit-3" size="14" />
                          <strong>새 템플릿 작성</strong>
                        </div>
                        <div style={{marginBottom: 10}}>
                          <label style={{display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4}}>강조유형</label>
                          <FormSelect value={this.kakaoEmphasisType} onChange={e => this.kakaoEmphasisType = e.target.value} style={{fontSize: 13}}>
                            <option value="none">기본형</option>
                            <option value="text">강조표기형</option>
                            <option value="image">이미지형</option>
                          </FormSelect>
                          {this.kakaoEmphasisType === 'text' && (
                            <div style={{display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8}}>
                              <FormInput style={{fontSize: 13}} placeholder="강조 표기 제목 (예: 적립 안내)" value={this.kakaoEmTitle}
                                         onChange={e => this.kakaoEmTitle = e.target.value} />
                              <FormInput style={{fontSize: 13}} placeholder="강조 표기 부제목 (필수)" value={this.kakaoEmSubtitle}
                                         onChange={e => this.kakaoEmSubtitle = e.target.value} />
                            </div>
                          )}
                          {this.kakaoEmphasisType === 'image' && (
                            <div style={{marginTop: 8}}>
                              <input type="file" accept="image/*" ref={this.kakaoFileRef} style={{display: 'none'}}
                                     onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) this.uploadKakaoImage(f); }} />
                              {this.kakaoImageUrl ? (
                                <div style={{display: 'flex', alignItems: 'flex-start', gap: 10}}>
                                  <img src={this.kakaoImageUrl} alt="강조 이미지" style={{maxWidth: 120, maxHeight: 120, borderRadius: 8, border: '1px solid #e2e8f0'}} />
                                  <div>
                                    <Button size="sm" theme="light" disabled={this.kakaoImageUploading}
                                            onClick={() => this.kakaoFileRef.current && this.kakaoFileRef.current.click()}>변경</Button>
                                    <button type="button" onClick={() => this.kakaoImageUrl = ''}
                                            style={{display: 'block', marginTop: 6, background: 'none', border: 'none', color: '#ef4444', fontSize: 12, cursor: 'pointer', padding: 0}}>제거</button>
                                  </div>
                                </div>
                              ) : (
                                <Button size="sm" theme="secondary" outline disabled={this.kakaoImageUploading}
                                        onClick={() => this.kakaoFileRef.current && this.kakaoFileRef.current.click()}>
                                  <FeatherIcon icon={this.kakaoImageUploading ? 'loader' : 'image'} size="12" style={{marginRight: 4}} />
                                  {this.kakaoImageUploading ? '업로드 중…' : '이미지 업로드'}
                                </Button>
                              )}
                              <div style={{fontSize: 11, color: '#94a3b8', marginTop: 4}}>권장: jpg/png, 최대 500KB · 카페24에 업로드됩니다</div>
                            </div>
                          )}
                        </div>
                        <div className="imsg-vars">
                          {type.vars.map(v => (
                            <button type="button" key={v} className="imsg-var-btn" onClick={() => this.kakaoNewContent = (this.kakaoNewContent || '') + '#{' + v + '}'}>
                              +#{'{' + v + '}'} <em>{VAR_DESC[v]}</em>
                            </button>
                          ))}
                        </div>
                        <FormTextarea
                          style={{height: '180px', fontSize: '14px', lineHeight: '1.6'}}
                          value={this.kakaoNewContent}
                          placeholder="#{name}님, ... (카카오 심사용 템플릿 내용)"
                          onChange={e => this.kakaoNewContent = e.target.value}
                        />
                        <div style={{display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap'}}>
                          <FormInput style={{flex: '1 1 140px', fontSize: 12}} placeholder="버튼명 (선택)" value={this.kakaoNewButtonName}
                                     onChange={e => this.kakaoNewButtonName = e.target.value} />
                          <FormInput style={{flex: '2 1 220px', fontSize: 12}} placeholder="버튼 링크 (선택, https://...)" value={this.kakaoNewButtonLink}
                                     onChange={e => this.kakaoNewButtonLink = e.target.value} />
                        </div>
                        <div className="edit-pane-actions">
                          <Button size="sm" theme="light"
                                  onClick={() => { this.kakaoEditorForType = null; this.resetKakaoEditor(); }}>
                            취소
                          </Button>
                          <Button size="sm" theme="primary" disabled={this.kakaoTplSaving || !(this.kakaoNewContent || '').trim()}
                                  onClick={() => this.addKakaoTemplate(type.key)}>
                            <FeatherIcon icon="check" size="12" style={{marginRight: '3px'}} />
                            {this.kakaoTplSaving ? '등록 중…' : '템플릿 생성 (심사요청)'}
                          </Button>
                        </div>
                        <div className="edit-pane-tip">
                          <FeatherIcon icon="info" size="12" />
                          치환 변수: <code>#&#123;name&#125;</code>, <code>#&#123;shop&#125;</code>, <code>#&#123;product&#125;</code>
                          ・ 작성 가이드는 <a href="https://kakaobusiness.gitbook.io/main/ad/infotalk/content-guide" target="_blank" rel="noopener noreferrer">여기</a>에서 확인
                        </div>
                      </div>
                    </Col>
                    {/* 우측: 카카오톡 채팅방 스타일 미리보기 */}
                    <Col lg="5" md="12">
                      <div className="preview-pane preview-pane-kakao">
                        <div className="preview-header">
                          <FeatherIcon icon="smartphone" size="14" />
                          <strong>카카오톡 미리보기</strong>
                        </div>
                        <div className="kakao-chat-frame">
                          <div className="kakao-chat-titlebar" aria-hidden="true">
                            <FeatherIcon icon="chevron-left" size="18" />
                            <div className="kakao-chat-title">
                              <span className="title-text">{storeName}</span>
                            </div>
                            <FeatherIcon icon="search" size="16" />
                            <FeatherIcon icon="menu" size="16" />
                          </div>
                          <div className="kakao-chat-body">
                            <div className="kakao-chat-date">오늘</div>
                            <div className="kakao-chat-msg-row">
                              <div className="kakao-chat-avatar">
                                <FeatherIcon icon="message-circle" size="20" />
                              </div>
                              <div className="kakao-chat-bubble-wrap">
                                <div className="kakao-chat-name">{storeName}</div>
                                <div className="kakao-chat-bubble-row">
                                  <div className="kakao-chat-bubble">
                                    <div className="kakao-bubble-tag">알림톡 도착</div>
                                    {this.kakaoEmphasisType === 'image' && this.kakaoImageUrl ? (
                                      <div className="kakao-bubble-image"><img src={this.kakaoImageUrl} alt="강조 이미지" /></div>
                                    ) : null}
                                    {this.kakaoEmphasisType === 'text' && (this.kakaoEmTitle || this.kakaoEmSubtitle) ? (
                                      <div style={{padding: '6px 2px 8px'}}>
                                        {this.kakaoEmTitle ? <div style={{fontSize: 16, fontWeight: 700, color: '#1f2937', lineHeight: 1.35}}>{this.kakaoEmTitle}</div> : null}
                                        {this.kakaoEmSubtitle ? <div style={{fontSize: 12, color: '#94a3b8', marginTop: 2}}>{this.kakaoEmSubtitle}</div> : null}
                                      </div>
                                    ) : null}
                                    <div className="kakao-bubble-text">
                                      <pre>{applyVars(this.kakaoNewContent, 'kakao', storeName) || '내용을 입력하면 미리보기에 표시됩니다.'}</pre>
                                    </div>
                                    {(this.kakaoNewButtonName || '').trim() ? (
                                      <div className="kakao-bubble-button">{this.kakaoNewButtonName}</div>
                                    ) : null}
                                  </div>
                                  <div className="kakao-chat-time">
                                    {moment().format('A h:mm').replace('AM', '오전').replace('PM', '오후')}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="kakao-chat-inputbar" aria-hidden="true">
                            <FeatherIcon icon="plus" size="18" />
                            <div className="kakao-chat-input-placeholder">메시지 입력</div>
                            <FeatherIcon icon="smile" size="18" />
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              ) : (
                <Button size="sm" theme="primary" outline style={{marginTop: 4}}
                        onClick={() => { this.kakaoEditorForType = type.key; this.resetKakaoEditor(); this.kakaoNewContent = SAMPLE_TEMPLATES[type.key] || ''; }}>
                  <FeatherIcon icon="plus" size="13" /> 새 템플릿 작성
                </Button>
              )
            ) : (
              <div style={{fontSize: 11, color: '#94a3b8', marginTop: 4}}>이 항목의 템플릿이 최대(3개)입니다. 새로 만들려면 기존 템플릿을 삭제하세요.</div>
            )}
          </>
        )}
      </div>
    );
  };

  renderChannelEditor = (type, ch) => {
    const t = this.types[type.key];
    const content = (t[ch] && t[ch].content) || '';
    const isKakao = ch === 'kakao';
    return (
      <div className="imsg-editor" key={ch}>
        <div className="imsg-editor-head">
          <span className="imsg-editor-ch">{ch === 'sms' ? '문자' : '알림톡'} 내용</span>
          {isKakao && <span className="imsg-editor-note">알림톡은 승인된 템플릿 형식(<code>#{'{변수}'}</code>)을 사용합니다.</span>}
        </div>
        <div className="imsg-vars">
          {type.vars.map(v => (
            <button type="button" key={v} className="imsg-var-btn" onClick={() => this.insertVar(type.key, ch, v)}>
              +{isKakao ? '#{' + v + '}' : '{' + v + '}'} <em>{VAR_DESC[v]}</em>
            </button>
          ))}
        </div>
        <div style={{display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start', marginTop: 8}}>
          {/* 입력 — 실제 문자 화면 크기 (모바일 너비/높이) */}
          <div style={{flex: '0 0 auto', width: 300, maxWidth: '100%'}}>
            <div style={{fontSize: 12, color: '#64748b', marginBottom: 6, fontWeight: 600}}>입력 <span style={{fontWeight: 400, color: '#94a3b8'}}>(실제 문자 화면 크기)</span></div>
            <textarea value={content}
                      placeholder="{name}님, ..."
                      onChange={e => this.setChannelContent(type.key, ch, e.target.value)}
                      style={{width: '100%', height: 360, fontSize: 14, lineHeight: 1.7, padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: 14, resize: 'none', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit'}} />
            <div style={{fontSize: 11, color: '#94a3b8', marginTop: 4, textAlign: 'right'}}>{content.length}자</div>
          </div>
          {/* 미리보기 — 모바일 디바이스 */}
          <div style={{flex: '0 0 auto'}}>
            <div style={{fontSize: 12, color: '#64748b', marginBottom: 6, fontWeight: 600}}>미리보기</div>
            <div style={{width: 300, border: '9px solid #111827', borderRadius: 32, overflow: 'hidden', background: '#fff', boxShadow: '0 8px 28px rgba(0,0,0,0.18)'}}>
              <div style={{background: '#111827', height: 24, display: 'flex', justifyContent: 'center'}}>
                <div style={{width: 96, height: 16, background: '#111827', borderRadius: '0 0 12px 12px'}} />
              </div>
              <div style={{background: '#f8fafc', padding: '10px 14px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#334155', borderBottom: '1px solid #e2e8f0'}}>문자 메시지</div>
              <div style={{background: '#fff', minHeight: 300, padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 8}}>
                <div style={{textAlign: 'center', fontSize: 11, color: '#9ca3af', marginBottom: 4}}>오늘</div>
                <div style={{alignSelf: 'flex-start', maxWidth: '85%', background: '#e9edf2', color: '#1f2937', padding: '11px 13px', borderRadius: '4px 16px 16px 16px', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                  {applyVars(content, ch, (this.teamUser && this.teamUser.store && this.teamUser.store.name) || '온리플샵') || <span style={{color: '#9ca3af'}}>문자 내용을 입력하면 여기에 표시됩니다</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  selectTab = (key) => {
    this.activeTab = key;
    if (key === 'history' && !this.historyLoaded) this.loadHistory(1);
  };

  // 발송 내역 조회 (통합메시지 — order_id 'IMSG_' 로깅분)
  loadHistory = async (page) => {
    this.historyLoading = true;
    this.historyLoaded = true;
    const store_id = this.teamUser.selStore_id;
    const shopNo = this.teamUser.selShopNo ? this.teamUser.selShopNo : 1;
    try {
      const res = await this.props.getDoc('getIntegratedMessageHistory', { store_id, shopNo, page: page || 1 });
      const data = (res && res.data) ? res.data : {};
      this.history = Array.isArray(data.docs) ? data.docs : [];
      this.historyPage = data.page || page || 1;
      this.historyTotalPages = data.totalPages || 1;
    } catch (e) {
      this.history = [];
    } finally {
      this.historyLoading = false;
    }
  };

  renderHistory = () => {
    const rows = this.history || [];
    const chLabel = (mt) => mt === 'KAKAO' ? '알림톡' : (mt === 'SMS' ? '문자' : (mt || '-'));
    const maskPhone = (p) => {
      const s = String(p || '');
      return s.length >= 7 ? s.slice(0, 3) + '****' + s.slice(-4) : s;
    };
    const statusBadge = (st) => {
      if (st === 'SENT') return <Badge theme="success">발송완료</Badge>;
      if (st === 'FAILED') return <Badge theme="danger">실패</Badge>;
      return <Badge theme="secondary">{st === 'PENDING' ? '대기' : (st || '-')}</Badge>;
    };
    return (
      <div className="imsg-history" style={{marginTop: 4}}>
        <div style={{overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff'}}>
          <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 13}}>
            <thead>
              <tr style={{background: '#f8fafc', textAlign: 'left', color: '#475569'}}>
                <th style={{padding: '10px 14px', whiteSpace: 'nowrap'}}>발송시각</th>
                <th style={{padding: '10px 14px'}}>채널</th>
                <th style={{padding: '10px 14px'}}>구분</th>
                <th style={{padding: '10px 14px'}}>수신자</th>
                <th style={{padding: '10px 14px', textAlign: 'right'}}>차감</th>
                <th style={{padding: '10px 14px'}}>상태</th>
              </tr>
            </thead>
            <tbody>
              {this.historyLoading ? (
                <tr><td colSpan={6} style={{padding: 24, textAlign: 'center', color: '#94a3b8'}}>불러오는 중…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} style={{padding: 32, textAlign: 'center', color: '#94a3b8'}}>발송 내역이 없습니다.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={r._id || i} style={{borderTop: '1px solid #f1f5f9'}}>
                  <td style={{padding: '10px 14px', whiteSpace: 'nowrap', color: '#334155'}}>{r.sentAt ? moment(r.sentAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                  <td style={{padding: '10px 14px'}}>{chLabel(r.messageType)}</td>
                  <td style={{padding: '10px 14px', color: '#475569'}}>{r.templateName || '-'}</td>
                  <td style={{padding: '10px 14px', color: '#475569'}}>{maskPhone(r.receiver)}</td>
                  <td style={{padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: '#1f2937'}}>{(r.messageQuotaUsed || 0) + '건'}</td>
                  <td style={{padding: '10px 14px'}}>{statusBadge(r.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {this.historyTotalPages > 1 && (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 12}}>
            <Button size="sm" theme="light" disabled={this.historyPage <= 1} onClick={() => this.loadHistory(this.historyPage - 1)}>이전</Button>
            <span style={{fontSize: 12, color: '#64748b'}}>{this.historyPage} / {this.historyTotalPages}</span>
            <Button size="sm" theme="light" disabled={this.historyPage >= this.historyTotalPages} onClick={() => this.loadHistory(this.historyPage + 1)}>다음</Button>
          </div>
        )}
      </div>
    );
  };

  // 테스트 발송 — 샘플 데이터로 실제 발송(쿼터 차감). order_id ^IMSG_TEST_ 로 내역에 기록됨.
  sendTest = (typeKey, channel) => {
    const phone = (this.testPhone || '').replace(/[^\d]/g, '');
    if (phone.length < 9) { toast.error('테스트 받을 전화번호를 입력해 주세요.'); return; }
    const key = typeKey + ':' + channel;
    if (this.testSendingKey) return;
    this.testSendingKey = key;
    const store_id = this.teamUser.selStore_id;
    const shopNo = this.teamUser.selShopNo ? this.teamUser.selShopNo : 1;
    let _this = this;
    this.props.updateDoc('sendTestIntegratedMessage', { store_id, shopNo, typeKey, channel, testPhone: this.testPhone, testName: '' }, function(result, errors) {
      _this.testSendingKey = null;
      if (errors && errors.length > 0) { toast.error(errors[0].message || '테스트 발송에 실패했습니다.'); return; }
      const data = result && result.data;
      if (data && Array.isArray(data.errors) && data.errors.length > 0) { toast.error(data.errors[0].message); return; }
      if (data && data.ok) { toast.success('테스트 발송되었습니다. (실제 메시지가 발송되어 잔량이 차감됩니다)'); return; }
      toast.error('테스트 발송에 실패했습니다.');
    });
  };

  renderCard = (type) => {
    const t = this.types[type.key];
    if (!t) return null;
    const on = !!t.enabled;
    return (
      <div className={`imsg-card ${on ? 'is-on' : ''}`} key={type.key}>
        <div className="imsg-card-head">
          <div className="imsg-card-title">
            <span className="imsg-card-name">{type.title}</span>
            <span className={`imsg-chip ${on ? 'is-on' : 'is-off'}`}>{on ? 'ON' : 'OFF'}</span>
          </div>
          <FormCheckbox toggle small checked={on} onChange={() => this.toggleEnabled(type.key)}>
            {on ? '사용' : '미사용'}
          </FormCheckbox>
        </div>
        <p className="imsg-card-desc">{type.desc}</p>

        {on && (
          <div className="imsg-card-body">
            {/* 채널 선택 */}
            <div className="imsg-row">
              <label className="imsg-row-label">발송 채널</label>
              <div className="imsg-channels">
                {CHANNELS.filter(c => !(SMS_HIDDEN && c.key === 'sms')).map(c => {
                  const checked = (t.channels || []).includes(c.key);
                  return (
                    <label key={c.key} className={`imsg-channel ${!c.enabled ? 'is-disabled' : ''} ${checked ? 'is-checked' : ''}`}>
                      <input type="checkbox" disabled={!c.enabled} checked={checked}
                             onChange={() => c.enabled && this.toggleChannel(type.key, c.key)} />
                      <span>{c.label}{!c.enabled && ' (준비중)'}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* 타입별 추가 설정 */}
            {type.extra === 'daysBefore' && (
              <div className="imsg-row">
                <label className="imsg-row-label">발송 시점</label>
                <div className="imsg-inline">
                  만료 <FormInput type="number" className="imsg-num" value={t.daysBefore}
                                  onChange={e => this.setField(type.key, { daysBefore: parseInt(e.target.value, 10) || 0 })} /> 일 전
                </div>
              </div>
            )}
            {type.extra === 'mileageExpire' && (
              <div className="imsg-row">
                <label className="imsg-row-label">유효기간 / 발송 시점</label>
                <div className="imsg-inline">
                  적립 후 <FormInput type="number" className="imsg-num" value={t.validityMonths}
                                    onChange={e => this.setField(type.key, { validityMonths: parseInt(e.target.value, 10) || 0 })} /> 개월 유효,
                  소멸 <FormInput type="number" className="imsg-num" value={t.daysBefore}
                                  onChange={e => this.setField(type.key, { daysBefore: parseInt(e.target.value, 10) || 0 })} /> 일 전 발송
                </div>
              </div>
            )}
            {type.extra === 'coupon' && (
              <div className="imsg-row">
                <label className="imsg-row-label">발급 쿠폰 / 시점</label>
                <div className="imsg-inline">
                  {this.coupons.length > 0 ? (
                    <FormSelect className="imsg-coupon" value={t.couponNo}
                                onChange={e => {
                                  const c = this.coupons.find(x => String(x.coupon_no) === e.target.value);
                                  this.setField(type.key, { couponNo: e.target.value, couponName: c ? c.coupon_name : '' });
                                }}>
                      <option value="">쿠폰 선택</option>
                      {this.coupons.map(c => <option key={c.coupon_no} value={c.coupon_no}>{c.coupon_name} ({c.coupon_no})</option>)}
                    </FormSelect>
                  ) : (
                    <FormInput className="imsg-coupon" placeholder="쿠폰번호 (목록 미로딩 시 직접 입력)"
                               value={t.couponNo} onChange={e => this.setField(type.key, { couponNo: e.target.value })} />
                  )}
                  <FormSelect className="imsg-trigger" value={t.issueTrigger}
                              onChange={e => this.setField(type.key, { issueTrigger: e.target.value })}>
                    {ISSUE_TRIGGERS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </FormSelect>
                </div>
              </div>
            )}

            {/* 채널별 템플릿: 문자=자유 입력, 알림톡=승인 템플릿 선택 */}
            {!SMS_HIDDEN && (t.channels || []).includes('sms') && this.renderChannelEditor(type, 'sms')}
            {(t.channels || []).includes('kakao') && this.renderKakaoEditor(type)}
            {(t.channels || []).filter(c => c === 'kakao' || (c === 'sms' && !SMS_HIDDEN)).length === 0 && (
              <div className="imsg-empty-ch">발송 채널을 선택하면 메시지 내용을 작성할 수 있습니다.</div>
            )}

            {/* 테스트 발송 */}
            {(t.channels || []).filter(c => c === 'kakao' || (c === 'sms' && !SMS_HIDDEN)).length > 0 && (
              <div className="imsg-row" style={{display: 'block', marginTop: 14, paddingTop: 12, borderTop: '1px dashed #e2e8f0'}}>
                <label className="imsg-row-label" style={{display: 'block', marginBottom: 6}}>테스트 발송</label>
                <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                  <FormInput style={{maxWidth: 220}} placeholder="테스트 받을 휴대폰 번호 (- 없이)"
                             value={this.testPhone} onChange={e => this.testPhone = e.target.value} />
                  {!SMS_HIDDEN && (t.channels || []).includes('sms') && (
                    <Button size="sm" theme="secondary" outline disabled={this.testSendingKey === type.key + ':sms'}
                            onClick={() => this.sendTest(type.key, 'sms')}>
                      <FeatherIcon icon="send" size="12" style={{marginRight: 3}} />
                      {this.testSendingKey === type.key + ':sms' ? '발송 중…' : '문자 테스트'}
                    </Button>
                  )}
                  {(t.channels || []).includes('kakao') && (
                    <Button size="sm" theme="secondary" outline disabled={this.testSendingKey === type.key + ':kakao'}
                            onClick={() => this.sendTest(type.key, 'kakao')}>
                      <FeatherIcon icon="send" size="12" style={{marginRight: 3}} />
                      {this.testSendingKey === type.key + ':kakao' ? '발송 중…' : '알림톡 테스트'}
                    </Button>
                  )}
                </div>
                <div style={{fontSize: 11, color: '#94a3b8', marginTop: 6}}>샘플 데이터로 실제 발송됩니다 · 잔량 차감(문자 단문 2 · 장문 5, 알림톡 1)</div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // 상단 채널/알림톡 현황 대시보드 — kakaoMsgSetting 의 status-dashboard 디자인과 일관
  renderChannelDashboard = () => {
    const quota = (this.teamUser && this.teamUser.store && this.teamUser.store.messageQuota) || 0;
    const quotaMax = 10000;
    const quotaPct = Math.min(100, Math.max(2, Math.round((quota / quotaMax) * 100)));
    const quotaLevel = quota <= 200 ? 'danger' : (quota <= 300 ? 'warn' : 'ok');
    const quotaAlertText = quotaLevel === 'danger' ? '잔량 부족' : (quotaLevel === 'warn' ? '충전 권장' : null);
    const connected = !!this.kakaoAuthed;
    return (
      <div className="kakao-msg-redesign">
        <Card className="status-dashboard mb-3">
          <CardBody>
            <Row className="align-items-center">
              {/* 보유 알림톡 (잔량) */}
              <Col md="5" sm="12" className={`status-item quota-item quota-${quotaLevel}`}>
                <div className="status-label">
                  <FeatherIcon icon="message-circle" size="14" />
                  <span>보유 알림톡</span>
                  {quotaAlertText && (
                    <Badge theme={quotaLevel === 'danger' ? 'danger' : 'warning'} className="quota-alert-badge">
                      <FeatherIcon icon="alert-triangle" size="10" style={{marginRight: 3}} />
                      {quotaAlertText}
                    </Badge>
                  )}
                </div>
                <div className="quota-row">
                  <div className="quota-numeric">
                    <strong className="quota-num">{quota.toLocaleString()}</strong>
                    <span className="unit">건</span>
                  </div>
                  <button type="button" className={`quota-cta quota-cta-${quotaLevel}`}
                          onClick={() => { window.location.href = '/msgOrder'; }}
                          aria-label={`알림톡 충전하기. 현재 보유 ${quota.toLocaleString()}건`}>
                    <FeatherIcon icon="zap" size="13" style={{marginRight: 4}} aria-hidden="true" />
                    충전하기
                  </button>
                </div>
                <div className="quota-progress">
                  <div className="quota-progress-track">
                    <div className="quota-progress-fill" style={{width: `${quotaPct}%`}} />
                  </div>
                  <div className="quota-progress-scale">
                    <span>0</span><span>5,000</span><span>10,000+</span>
                  </div>
                </div>
              </Col>

              {/* 카카오 채널 정보 */}
              <Col md="7" sm="12" className="status-item channel-item">
                <div className="status-label">
                  <FeatherIcon icon="link" size="14" />
                  <span>카카오 채널</span>
                  {connected ? (
                    <Badge theme="success" className="channel-status-badge channel-connected">
                      <FeatherIcon icon="check-circle" size="10" style={{marginRight: 3}} />
                      연결됨
                    </Badge>
                  ) : (
                    <Badge theme="warning" className="channel-status-badge channel-disconnected">
                      <FeatherIcon icon="alert-circle" size="10" style={{marginRight: 3}} />
                      미연결
                    </Badge>
                  )}
                </div>
                {connected ? (
                  <div className="channel-info-list">
                    {this.kakaoPlusid && (
                      <div className="channel-info-row">
                        <FeatherIcon icon="at-sign" size="11" />
                        <span className="channel-info-key">채널 ID</span>
                        <span className="channel-info-val">{this.kakaoPlusid}</span>
                      </div>
                    )}
                    {this.kakaoPhone && (
                      <div className="channel-info-row">
                        <FeatherIcon icon="phone" size="11" />
                        <span className="channel-info-key">관리자</span>
                        <span className="channel-info-val">{this.kakaoPhone}</span>
                      </div>
                    )}
                    <div className="channel-info-actions">
                      <a href="/kakaoMsgSetting" className="channel-delete-btn" style={{textDecoration: 'none'}}
                         title="알림톡 채널/템플릿 설정으로 이동">
                        <FeatherIcon icon="settings" size="11" style={{marginRight: 4}} aria-hidden="true" />
                        알림톡 설정 관리
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="channel-info-list">
                    <div className="channel-info-empty" style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                      알림톡 발송을 위해 카카오 채널 연결이 필요합니다.
                      <a href="/kakaoMsgSetting" style={{display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 9px', background: '#5664D2', color: '#fff', borderRadius: 5, fontSize: 11, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap'}} title="알림톡 설정으로 이동">
                        채널 연결하기 <FeatherIcon icon="arrow-right" size="11" />
                      </a>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>
      </div>
    );
  };

  render() {
    const cards = TYPES.filter(t => t.tab === this.activeTab);
    return (
      <div className="main-content imsg-page">
        <PageTitle />
        {this.loaded && this.renderChannelDashboard()}

        <div className="imsg-guide">
          <FeatherIcon icon="info" size="14" aria-hidden="true" />
          <span>마일리지·쿠폰·배송·리뷰 이벤트가 발생하면 <strong>문자/알림톡</strong>으로 고객에게 자동 안내합니다. 발송 건은 보유 메시지에서 차감됩니다.</span>
        </div>

        {/* 탭 */}
        <div className="imsg-tabs">
          {TABS.map(tab => (
            <div key={tab.key} className={`imsg-tab ${this.activeTab === tab.key ? 'active' : ''}`}
                 onClick={() => this.selectTab(tab.key)}>
              <FeatherIcon icon={tab.icon} size="16" aria-hidden="true" />
              <span>{tab.label}</span>
            </div>
          ))}
        </div>

        {/* 마일리지 탭 — 적립 연동 상태 안내 배너 */}
        {this.activeTab === 'mileage' && (
          <div className="imsg-status-banner">
            <div className="imsg-status-banner__head">
              <FeatherIcon icon="gift" size="16" aria-hidden="true" />
              <strong>마일리지 적립 연동 안내</strong>
              <span className="imsg-status-banner__pill">연동 활성</span>
            </div>
            <ul className="imsg-status-banner__list">
              <li>
                <FeatherIcon icon="check-circle" size="14" aria-hidden="true" />
                <span><strong>적립금 적립</strong> — 카페24 적립금과 실시간 연동됩니다. 회원이 작성한 리뷰는 스토어의 <em>마일리지 자동적립</em> 설정이 켜져 있으면 자동 적립되고, 그 외에는 관리자 수동 지급으로 처리됩니다.</span>
              </li>
              <li>
                <FeatherIcon icon="check-circle" size="14" aria-hidden="true" />
                <span><strong>적립 안내 발송</strong> — 적립이 완료되는 시점에, 아래 <em>‘마일리지 적립 안내’</em>를 켜 두면 고객에게 자동으로 발송됩니다.</span>
              </li>
              <li>
                <FeatherIcon icon="check-circle" size="14" aria-hidden="true" />
                <span><strong>소멸 안내 발송</strong> — 매일 오전 9시, <em>‘마일리지 소멸 안내’</em>를 켜 둔 경우 소멸 예정 적립금(온리플 리뷰 적립분)에 대해 자동으로 발송됩니다.</span>
              </li>
            </ul>
            <div className="imsg-status-banner__foot">
              <FeatherIcon icon="alert-circle" size="13" aria-hidden="true" />
              <span>안내 메시지를 켜지 않으면 적립은 정상 처리되어도 고객 안내는 발송되지 않습니다. 발송 건은 보유 메시지에서 차감됩니다.</span>
            </div>
          </div>
        )}

        {/* 내역 탭: 발송 내역 테이블 / 그 외: 항목별 설정 카드 */}
        {this.activeTab === 'history' ? (
          this.renderHistory()
        ) : (
          <div className="imsg-cards">
            {this.loaded ? cards.map(this.renderCard) : <div className="imsg-loading">불러오는 중…</div>}
          </div>
        )}

        {/* 저장 바 — 앱 표준 wbp-sticky-save 패턴 (위젯/카카오 설정과 동일, dirty 일 때만 노출) */}
        {this.settingsDirty && (
          <div className="wbp-sticky-save wbp-sticky-save--top" role="status" aria-live="polite">
            <div className="wbp-sticky-save__msg">
              <FeatherIcon icon="alert-circle" size="16" />
              {this.getDirtyLabels().length > 0 ? (
                <span><strong>{this.getDirtyLabels().length}개 항목</strong>에 저장되지 않은 변경사항이 있습니다.</span>
              ) : (
                <span>저장되지 않은 변경사항이 있습니다.</span>
              )}
              {this.getDirtyLabels().length > 0 && (
                <span className="wbp-sticky-save__tags">
                  {this.getDirtyLabels().map(label => (
                    <span key={label} className="wbp-sticky-save__tag">{label}</span>
                  ))}
                </span>
              )}
            </div>
            <div className="wbp-sticky-save__actions">
              <Button theme="light" size="sm" onClick={() => this.revertSettings()}
                      aria-label="변경사항 취소하고 이전 상태로 되돌리기">
                되돌리기
              </Button>
              <Button theme="primary" size="sm" disabled={this.saving || !this.loaded} onClick={() => this.save()}
                      aria-label="통합메시지 설정 변경사항 저장">
                {this.saving ? '저장 중…' : '변경사항 저장'}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default IntegratedMessage;
